import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Calculator, 
  ArrowRight, 
  Info, 
  Shield, 
  Clock, 
  ChevronDown, 
  DollarSign, 
  Percent, 
  AlertCircle, 
  CheckCircle2, 
  FileText, 
  Bell, 
  ArrowLeft,
  Building2,
  Phone,
  Mail,
  MapPin,
  X
} from 'lucide-react';
import LoanOnboarding from '../../components/LoanOnboarding';
import FAQ from '../../components/FAQ';

// Bank data with loan products
const bankData = {
  'garanti-bankasi': {
    id: 'garanti-bankasi',
    name: 'Garanti BBVA',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/2cf1c9fe-fbff-4874-a365-fd63455d078c.png',
    color: 'green',
    contact: {
      phone: '0850 222 0 333',
      email: 'iletisim@garantibbva.com.tr',
      website: 'https://www.garantibbva.com.tr'
    },
    loanProducts: {
      'ihtiyac-kredisi': [
        {
          title: 'Garanti BBVA İhtiyaç Kredisi',
          rate: '2.99',
          term: '36',
          amount: '300.000',
          features: ['%0 dosya masrafı', 'Anında onay', '3 ay erteleme'],
          benefits: [
            'Kredi tutarı 300.000 TL\'ye kadar',
            '36 aya varan vade seçenekleri',
            'İlk 3 ay ödemesiz dönem',
            'Dosya masrafı yok',
            'Online başvuru imkanı'
          ],
          requirements: [
            'T.C. vatandaşı olmak',
            '18 yaşını doldurmuş olmak',
            'Düzenli gelir sahibi olmak',
            'Kredi notu uygun olmak'
          ],
          documents: [
            'Nüfus cüzdanı',
            'Gelir belgesi',
            'İkametgah belgesi',
            'Banka hesap özeti'
          ]
        },
        {
          title: 'Garanti BBVA Hızlı İhtiyaç Kredisi',
          rate: '3.49',
          term: '24',
          amount: '150.000',
          features: ['5 dakikada onay', 'Masrafsız kullanım', 'Esnek ödeme'],
          benefits: [
            'Kredi tutarı 150.000 TL\'ye kadar',
            '24 aya varan vade seçenekleri',
            '5 dakikada kredi onayı',
            'Hiçbir masraf yok',
            'Mobil başvuru'
          ],
          requirements: [
            'T.C. vatandaşı olmak',
            '18 yaşını doldurmuş olmak',
            'Düzenli gelir sahibi olmak'
          ],
          documents: [
            'Nüfus cüzdanı',
            'Gelir belgesi',
            'İkametgah belgesi'
          ]
        }
      ],
      'konut-kredisi': [
        {
          title: 'Garanti BBVA Konut Kredisi',
          rate: '2.49',
          term: '120',
          amount: '5.000.000',
          features: ['%90 finansman', 'Ekspertiz indirimi', 'Sigorta avantajı'],
          benefits: [
            'Evin değerinin %90\'ına kadar kredi',
            '120 aya varan vade seçenekleri',
            'Ücretsiz ekspertiz hizmeti',
            'Özel sigorta indirimleri',
            'Esnek ödeme planı'
          ],
          requirements: [
            'T.C. vatandaşı olmak',
            '18 yaşını doldurmuş olmak',
            'Düzenli gelir sahibi olmak',
            'Tapu sahibi olmak'
          ],
          documents: [
            'Nüfus cüzdanı',
            'Gelir belgesi',
            'Tapu fotokopisi',
            'İkametgah belgesi'
          ]
        }
      ],
      'tasit-kredisi': [
        {
          title: 'Garanti BBVA Taşıt Kredisi',
          rate: '3.19',
          term: '48',
          amount: '800.000',
          features: ['Hızlı onay', 'Kasko indirimi', 'Esnek ödeme'],
          benefits: [
            '48 aya varan vade seçenekleri',
            'Araç değerinin %70\'ine kadar kredi',
            'Özel kasko indirimleri',
            'Esnek ödeme planı',
            'Hızlı onay süreci'
          ],
          requirements: [
            'T.C. vatandaşı olmak',
            '18 yaşını doldurmuş olmak',
            'Düzenli gelir sahibi olmak'
          ],
          documents: [
            'Nüfus cüzdanı',
            'Gelir belgesi',
            'Araç ruhsatı',
            'İkametgah belgesi'
          ]
        }
      ]
    },
    campaigns: [
      {
        title: 'Yeni Müşteri Kampanyası',
        description: 'İlk krediye özel %0 faiz fırsatı!',
        endDate: '31.12.2024',
        conditions: [
          'İlk kez Garanti BBVA müşterisi olacaklar için geçerlidir',
          '3 ay vade ile kullanılabilir',
          'Kredi notu 1500 ve üzeri olan müşteriler için geçerlidir'
        ]
      }
    ]
  },
  'akbank': {
    id: 'akbank',
    name: 'Akbank',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/a80c0972-6830-4dc4-b3bc-e236c7fbb5ea.png',
    color: 'red',
    contact: {
      phone: '0850 222 00 00',
      email: 'iletisim@akbank.com',
      website: 'https://www.akbank.com'
    },
    loanProducts: {
      'ihtiyac-kredisi': [
        {
          title: 'Akbank İhtiyaç Kredisi',
          rate: '3.99',
          term: '36',
          amount: '500.000',
          features: ['Masrafsız kullanım', 'Anında onay', '3 ay erteleme'],
          benefits: [
            'Kredi tutarı 500.000 TL\'ye kadar',
            '36 aya varan vade seçenekleri',
            'İlk 3 ay ödemesiz dönem',
            'Dosya masrafı yok',
            'Dijital başvuru'
          ],
          requirements: [
            'T.C. vatandaşı olmak',
            '18 yaşını doldurmuş olmak',
            'Düzenli gelir sahibi olmak'
          ],
          documents: [
            'Nüfus cüzdanı',
            'Gelir belgesi',
            'İkametgah belgesi'
          ]
        }
      ]
    },
    campaigns: [
      {
        title: 'Hoş Geldin Kredisi',
        description: '%0 faizli 10.000 TL kredi fırsatı',
        endDate: '31.12.2024',
        conditions: [
          'İlk kez Akbank müşterisi olacaklar için geçerlidir',
          '3 ay vade ile kullanılabilir'
        ]
      }
    ]
  }
};

// Loan type mapping
const loanTypeMapping = {
  'ihtiyac-kredisi': {
    title: 'İhtiyaç Kredisi',
    description: 'Nakit ihtiyaçlarınız için uygun faizli kredi seçenekleri',
    icon: '💰'
  },
  'konut-kredisi': {
    title: 'Konut Kredisi',
    description: 'Ev sahibi olmak için uzun vadeli finansman çözümleri',
    icon: '🏠'
  },
  'tasit-kredisi': {
    title: 'Taşıt Kredisi',
    description: 'Araç alımlarınız için özel kredi imkanları',
    icon: '🚗'
  }
};

const BankLoanPage = () => {
  const { bankId, loanType } = useParams<{ bankId: string; loanType: string }>();
  const navigate = useNavigate();
  const [selectedLoan, setSelectedLoan] = useState<string | null>(null);
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [expandedDetails, setExpandedDetails] = useState<string[]>([]);
  const [isConditionsModalOpen, setIsConditionsModalOpen] = useState(false);

  const bank = bankData[bankId as keyof typeof bankData];
  const loanTypeInfo = loanTypeMapping[loanType as keyof typeof loanTypeMapping];

  const loanProducts = bank?.loanProducts?.[loanType as keyof typeof bank.loanProducts] || [];

  const handleCalculate = (loanTitle: string) => {
    setSelectedLoan(loanTitle);
    setIsCalculatorOpen(true);
  };

  const toggleDetails = (key: string) => {
    setExpandedDetails(prev => 
      prev.includes(key) 
        ? prev.filter(item => item !== key)
        : [...prev, key]
    );
  };

  if (!bank || !loanTypeInfo) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800 mb-4">Sayfa Bulunamadı</h1>
          <p className="text-slate-600 mb-6">Aradığınız banka veya ürün sayfası bulunamadı.</p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors"
          >
            <ArrowLeft size={20} />
            Ana Sayfaya Dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#F7BA45]/20 to-slate-50 py-4 md:py-6">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-start gap-4 sm:gap-6 mb-4 md:mb-6">
              <img 
                src="https://cdn.hangikredi.com/images/bankdigitalonboarding/7accd65f-06ae-48b1-875e-6d9827590be6.png"
                alt={bank.name}
                className="h-12 sm:h-16 object-contain flex-shrink-0"
              />
              <div className="flex-1">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800 mb-2">
                  {bank.name} {loanTypeInfo.title}
                </h1>
                <p className="text-sm sm:text-base text-slate-600">
                  {bank.name} {loanTypeInfo.description.toLowerCase()}
                </p>
              </div>
            </div>
            
            {/* Desktop Banner - Below */}
            <div className="hidden lg:block w-full mt-4">
              <img 
                src="https://mukayese.com/img/ume/2025/07/15/akbank-large.jpg" 
                alt="Akbank Banner"
                className="w-full h-auto rounded-lg"
              />
            </div>


            {/* Campaigns */}
            {bank.campaigns && bank.campaigns.length > 0 && (
              <div className="grid grid-cols-1 gap-4 mb-4 md:mb-6 mt-4 md:mt-8">
                {bank.campaigns.map((campaign, index) => (
                  <div 
                    key={index}
                    className="bg-white rounded-xl p-4 md:p-6 border border-slate-200 hover:border-[#F7BA45]/50 transition-all duration-300 hover:shadow-lg w-full"
                  >
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                      <div className="bg-[#F7BA45]/10 p-2 md:p-3 rounded-lg">
                        <Bell className="h-6 w-6 text-[#F7BA45]" />
                      </div>
                      <div className="flex-1 flex items-start gap-3">
                        <div className="flex-1">
                        <h3 className="text-sm sm:text-base font-semibold text-slate-800 mb-2">%0 Faizli 90.000 TL'ye varan Nakit Fırsatı!</h3>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Clock size={14} />
                          <span>Son Geçerlilik: {campaign.endDate}</span>
                        </div>
                      </div>
                      </div>
                      </div>
                      <div className="w-full">
                        <button className="w-full bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-lg text-sm sm:text-base">
                          BAŞVUR
                          <ArrowRight size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loan Products */}
      <div className="container mx-auto px-4 py-4 md:py-8">
        <div className="max-w-7xl mx-auto">
          {loanProducts.length > 0 ? (
            <div className="grid grid-cols-1 gap-6">
              {loanProducts.map((product, index) => (
                <div key={index} className="bg-white rounded-xl p-4 md:p-6 border border-slate-200 hover:border-[#F7BA45]/50 transition-all duration-300 hover:shadow-lg">
                  <div className="flex flex-col items-center text-center gap-4 mb-2 md:mb-6">
                    <div className="flex-1">
                      <h3 className="text-lg md:text-xl font-semibold text-slate-800 mb-2">{product.title}</h3>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {product.features.map((feature, featureIndex) => (
                          <span key={featureIndex} className="bg-[#F7BA45]/10 text-[#F7BA45] px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium">
                            {feature}
                          </span>
                        ))}
                      </div>
                      <div className="flex flex-col lg:flex-row gap-4 mb-4">
                        <div className="w-full lg:flex-1 grid grid-cols-3 gap-2 md:gap-4 bg-slate-100 p-3 md:p-4 rounded-xl border border-slate-200 shadow-sm relative">
                          <div className="text-center relative">
                            <div className="text-xl md:text-3xl font-bold text-black">%0</div>
                            <div className="text-xs md:text-sm font-medium text-slate-700">Faiz Oranı</div>
                            <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-px h-6 md:h-8 bg-slate-300"></div>
                          </div>
                          <div className="text-center relative">
                            <div className="text-xl md:text-3xl font-bold text-black">3 Ay</div>
                            <div className="text-xs md:text-sm font-medium text-slate-700">Vade Süresi</div>
                            <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-px h-6 md:h-8 bg-slate-300"></div>
                          </div>
                          <div className="text-center">
                            <div className="text-xl md:text-3xl font-bold text-black">90.000</div>
                            <div className="text-xs md:text-sm font-medium text-slate-700">TL'ye Kadar</div>
                          </div>
                        </div>
                        <button className="w-full lg:w-48 bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 px-4 md:px-6 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl whitespace-nowrap text-sm md:text-base">
                          BANKAYA GİT
                          <ArrowRight size={16} className="md:hidden" />
                          <ArrowRight size={18} className="hidden md:block" />
                        </button>
                      </div>
                      
                      {/* Info Section */}
                      <div className="mt-4 p-3 md:p-4 bg-blue-50 border border-blue-200 rounded-lg relative">
                        <div className="flex items-start gap-2">
                          <Info size={14} className="md:hidden text-blue-600 flex-shrink-0 mt-0.5" />
                          <Info size={16} className="hidden md:block text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="text-xs md:text-sm text-blue-700">
                            <p className="mb-1">3 Ay vadeli 65.000 TL'ye varan taksitli artı para</p>
                            <p>3 Ay vadeli 25.000 TL'ye varan taksitli avans</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Conditions Button - Centered at bottom */}
                      <div className="mt-4 text-center">
                        <button
                          onClick={() => setIsConditionsModalOpen(true)}
                          className="text-xs md:text-sm text-slate-500 hover:text-slate-700 font-medium underline"
                        >
                          Koşullar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl p-6 md:p-8 text-center">
              <div className="text-slate-400 mb-4">
                <Building2 size={32} className="md:hidden mx-auto" />
                <Building2 size={48} className="hidden md:block mx-auto" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-slate-800 mb-2">
                Henüz Ürün Bulunmuyor
              </h3>
              <p className="text-sm md:text-base text-slate-600 mb-4 md:mb-6">
                {bank.name} için {loanTypeInfo.title.toLowerCase()} ürünleri henüz eklenmemiş.
              </p>
              <Link 
                to={`/${loanType}`}
                className="inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors text-sm md:text-base"
              >
                <ArrowLeft size={16} className="md:hidden" />
                <ArrowLeft size={20} className="hidden md:block" />
                {loanTypeInfo.title} Sayfasına Dön
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white py-6 md:py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-6 md:mb-8">
              <h2 className="text-xl md:text-3xl font-bold text-slate-800 mb-2 md:mb-4">
                {bank.name} {loanTypeInfo.title} Hakkında Sıkça Sorulan Sorular
              </h2>
              <p className="text-sm md:text-base text-slate-600">
                {bank.name} {loanTypeInfo.title.toLowerCase()} ürünleri hakkında merak ettiğiniz sorular
              </p>
            </div>
            <FAQ />
          </div>
        </div>
      </div>

      {/* Loan Calculator Modal */}
      {selectedLoan && (
        <LoanOnboarding
          isOpen={isCalculatorOpen}
          onClose={() => {
            setIsCalculatorOpen(false);
            setSelectedLoan(null);
          }}
          loanAmount={50000}
          loanTerm={12}
        />
      )}

      {/* Conditions Modal */}
      {isConditionsModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
            onClick={() => setIsConditionsModalOpen(false)}
          />
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div 
              className="relative bg-white rounded-2xl w-full max-w-4xl shadow-2xl max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-slate-900">
                    Kampanya Koşulları
                  </h2>
                  <button
                    onClick={() => setIsConditionsModalOpen(false)}
                    className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                  >
                    <X size={20} className="text-slate-500" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                <div className="space-y-8">
                  {/* 65.000 TL Faizsiz Taksitli Artı Para */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">
                      65.000 TL Faizsiz Taksitli Artı Para Kampanya Koşulları:
                    </h3>
                    <div className="space-y-3 text-sm text-slate-600">
                      <p>• Kampanyadan 16 Temmuz – 15 Ağustos 2025 tarihleri arasında Akbank uygulamasını indirerek, "Akbank Mobil'i indirip "Akbanklı olmak istiyorum" adımından ilerleyerek yalnızca ilk defa ve uzaktan müşterimi edinimi* sürecini tamamlayan müşteriler faydalanabilir.</p>
                      
                      <p>• Artı Para başvurusu "Akbanklı Ol" sürecinin tamamlanması ardından Akbank Mobil üzerinden gerçekleştirilebilir.</p>
                      
                      <p>• Mobilden Akbanklı olan yeni müşteriler, müşteri olduktan sonra maksimum 15 gün içinde Artı Paraya başvurabilir. Kurye süreci ile işlemlerini tamamlayacak olan ve ilk defa Akbanklı olacak müşteriler, belge işlemlerinin 14 Ağustos 2025'e kadar tamamlanması ve evrakların bankaya gelmesi ile birlikte Akbank Mobil'e girerek başvurularını yapabilirler.</p>
                      
                      <p>• 65.000 TL'ye ve 3 aya varan vade ile kullanılabilecek %0 faizli Taksitli Artı Para kampanyası, Mobilden Akbanklı ol sürecini tamamlayan müşterilere 15 gün geçerli olacak şekilde tarafınıza tanımlanacaktır.</p>
                      
                      <p>• Kampanya koşulları çerçevesinde, resmî tatil ve hafta sonlarında Mobil'den Akbanklı olan katılımcılar için tanımlama süreci, takip eden ilk iş günü itibarıyla başlayacak ve artı 3 iş günü içinde sonuçlanacaktır.</p>
                      
                      <p>• Kampanya koşullarını sağlayan müşteriler, Artı Para limitleri tanımlanmasının ardından 2 iş günü içerisinde kampanyadan yararlanabilir hale gelecektirler.</p>
                      
                      <p>• "Akbanklı Olma süreciniz, "Akbanklı Olmak İstiyorum" butonuna tıklamanız, sonrasında kişisel bilgilerinizi doldurmanız ve telefonunuza gelen cep şifreyi başarılı girmeniz itibarıyla başlar.</p>
                      
                      <p>• Kampanyadan 16 Temmuz – 15 Ağustos 2025 tarihleri arasında banka herhangi bir ürün/hizmetini aktif olarak kullanmayan ve bankada inaktif statüde yer alan müşterilerde, Akbank Mobil'i indirip "Akbanklı olmak istiyorum" butonu üzerinden adımları tamamlayarak / aktifleşerek kampanyadan faydalanabilir.</p>
                      
                      <p>• Kampanya kapsamındaki katılımcıların, SMS'te belirtilen tarihe kadar yapacakları 65.000 TL'ye kadar ve 2-3 ay vadedeki Taksitli Artı Para işlemlerinde indirimli %0 aylık akdi faiz oranı geçerli olacaktır. Belirtilen tutar ve vade aralığı dışındaki tüm Artı Para taleplerinde %5 (KKDF ve BSMV hariç) faiz oranı geçerli olacaktır.</p>
                      
                      <p>• Söz konusu başvurulan Artı Para limitleri, Akbank tarafından değerlendirilerek olumlu bulunursa karşılanacaktır. Artı Para başvurusu değerlendirilirken müşterinin Banka, KKB vb. kaynaklardaki verileri kullanılarak, Banka kredi politikalarına istinaden haciz, istihbarat olumsuzluğu olup olmadığı, mevcut borçlarını düzenli ödeyip ödemediği, gelirine göre borçluluk durumu, Banka kredi risk modeli sonuçları vb. kriterler göz önünde bulundurulur.</p>
                      
                      <p>• Başvuruların olumlu değerlendirilmesi sonrası onaylanan Artı Para limitiniz Akbank Mobil, Akbank İnternet, Müşteri İletişim Merkezi ve Şube'ler aracılığı ile kullanılabilir.</p>
                      
                      <p>• Kampanya kapsamında kullanılacak minimum Taksitli Artı Para tutarı 500 TL, maksimum tutar ise 65.000 TL, maksimum vade ise 3 aydır.</p>
                      
                      <p>• Kampanyadan yalnızca gerçek bireysel Artı Para sahibi müşteriler faydalanacaktır.</p>
                      
                      <p>• Mobilden Akbanklı olan müşteriler avantajlı Taksitli Artı Para kampanyasından sadece bir kere yararlanabilir.</p>
                      
                      <p>• Bankada izlemede veya takipte olan müşteriler, hesap üzerinde para çıkışına engel blokesi olan müşteriler ve bankamız KVKK izni olmayan müşteriler kampanyadan faydalanamazlar.</p>
                      
                      <p>• Bankadaki Okul Ailem Artı Para hesaplarına kampanya tanımı yapılamamaktadır.</p>
                      
                      <p>• Taksit ödeme tarihi her ayın 15'i olup, ilk taksit tarihi kullanım yapılan ayı takip eden ayın 15'idir. Taksit tarihinde, taksit tutarı kredi limitinin bağlı olduğu vadesiz mevduat hesabından otomatik olarak tahsil edilir. Tahsilat sırasında hesabınızda yeterli bakiye bulunmadığında Artı Para limitinizden tahsilat gerçekleştirilir.</p>
                      
                      <p>• Hesap bakiyenizin eksiye düştüğü ve de Artı Para kullanımı olduğu durumlarda, son ödeme tarihi Artı Para'nızın asgari borcunun ödenmesi için belirlenmiş tarihtir. Artı Para ve Taksitli Artı Para kullanımınıza bağlı geri ödemelerinizi Akbank hesap özetinizden kolaylıkla takip edebilirsiniz. Artı Para borcunuza dair asgari ödeme tutarının son ödeme tarihine kadar ödenmemesi durumunda Artı Para krediniz gecikmeli hale gelir, Artı Paranızın kullanıma açık kalması için 14 gün içinde asgari tutarın ödenmesi gerekir.</p>
                      
                      <p>• Bankanın kampanya koşullarını değiştirme, ek bilgi ve belge isteme ve kampanyayı durdurma hakkına sahip olduğunu hatırlatırız.</p>
                    </div>
                  </div>

                  {/* 25.000 TL'ye Varan Faizsiz Taksitli Avans */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">
                      25.000 TL'ye Varan Faizsiz Taksitli Avans Kampanya Koşulları:
                    </h3>
                    <div className="space-y-3 text-sm text-slate-600">
                      <p>• Kampanyadan Akbank bireysel kredi kartı olmayıp, Kampanyadan 16 Temmuz – 15 Ağustos 2025 tarihleri arasında Akbank uygulamasını indirerek, "Akbank Mobil'i indirip "Akbanklı olmak istiyorum" adımından ilerleyerek yalnızca ilk defa ve uzaktan müşterimi edinimi* sürecini tamamlayan müşteriler faydalanabilir. arasında internet sitesi üzerinden ilk kez Axess Troy asıl kart başvurusu yapan ve 31 Ağustos'a kadar kartı onaylanan Akbanklılar yararlanabilir.</p>
                      
                      <p>• Başvurusu onaylanan Akbanklılar 30 Eylül2025 tarihine kadar, taksitli avans limitleri doğrultusunda, maximum 25.000 TL tutarında ve 3 ay vadeyle %0 faizli Taksitli Avans kampanyasından yararlanabilir.</p>
                      
                      <p>• Kampanyadan yalnızca internet sitesi üzerinden kredi kartı başvurusunu tamamlayan Akbanklılar yararlanabilir.</p>
                      
                      <p>• Kampanyadan ilk kez Axess Troy asıl kredi kartı başvurusu yapan Akbanklılar yararlanabilir. 1 Temmuz 2025 tarihinden önce açık Axess, Wings veya Free kartı olan müşteriler kampanyadan yararlanamaz.</p>
                      
                      <p>• Kredi kartı başvurusu onaylanan Akbanklıların kullanılabilir taksitli avans limitleri kapsamındaki sıfır faizli Taksitli Avans kampanya tanımlamaları, kredi kartı başvuru onay tarihinden 2 iş günü sonra gerçekleşecektir.</p>
                      
                      <p>• Kampanya kapsamındaki katılımcıların, belirtilen tarihe kadar yapacakları 25.000 TL'ye kadar ve 3 ay vadedeki Taksitli Avans işlemlerinde indirimli %0 aylık akdi faiz oranı geçerli olacaktır. Belirtilen tutar ve vade aralığı dışındaki tüm Taksitli Avans taleplerinde %5 (KKDF ve BSMV hariç) faiz oranı geçerli olacaktır.</p>
                      
                      <p>• Bireysel kartlara ait Taksitli Avans işlemlerinde ücret alınmamaktadır.</p>
                      
                      <p>• Her Akbanklı kredi kartı açılışından sonra taksitli avans limitleri doğrultusunda, kampanya süresi boyunca sadece 1 kez sıfır faizli, Taksitli Avans kullanabilir.</p>
                      
                      <p>• Taksitli Avans, işlem başına en az 200 TL tutarında kullanılabilir. ATM'lerde ise tek seferde 4.000 TL ile sınırlandırılmıştır.</p>
                      
                      <p>• Kullanılabilecek Taksitli Avans tutarı; kartın kullanılabilir nakit avans limiti, tek işlem limiti ve günlük işlem limiti ile sınırlandırılmıştır.</p>
                      
                      <p>• Kredi kartı başvurusu ve taksitli avans başvurusu değerlendirilirken müşterinin Banka, KKB vb. kaynaklardaki verileri kullanılarak, diğer banka kredi kartı limitleri, Banka kredi politikalarına istinaden haciz, istihbarat olumsuzluğu olup olmadığı, mevcut borçlarını düzenli ödeyip ödemediği, gelirine göre borçluluk durumu, Banka kredi risk modeli sonuçları vb. kriterler göz önünde bulundurulur.</p>
                      
                      <p>• Fiziki kartlar ile Taksitli Avans kampanyasından faydalanabilmek için kredi kartının şifresinin belirlenmesi ve kullanıma açık olması gerekmektedir.</p>
                      
                      <p>• Kampanyaya sadece Axess Troy bireysel kredi kartları dahildir. Wings, Free, Ticari kredi kart, ek kart ve sanal kart başvuruları dahil değildir.</p>
                      
                      <p>• Axess Troy Kredi Kartı sahibi olan müşteriler 1 yıl kart ücreti ödemeyecektir.</p>
                      
                      <p>• Ekstrenin asgari borcunun son ödeme tarihine kadar hiç ödenmemesi veya kısmi olarak ödenmesi durumunda; hesap kesim tarihi ile son ödeme tarihi arasında ödenmemiş anapara üzerinden akdi faiz oranı, son ödeme tarihinden borç kapanana kadar ödenmemiş asgari tutar üzerinden gecikme aylık akdi faiz oranı ve asgari tutarı aşan anapara tutarı üzerinden ise akdi faiz oranının işletileceğini bildirmek isteriz.</p>
                      
                      <p>• Taksitli Avans işlemlerinde vadesi gelmemiş bir veya birden çok taksitin ödemesi yapılabilir veya borçların tamamı erken ödenebilir. Bu durumda erken ödenen miktara ve gün sayısına göre faiz ve diğer maliyetlerde indirim yapılır. Erken ödeme için şubelerimiz veya Müşteri İletişim Merkezi aracılığıyla erken/kısmi ödeme işlemine ilişkin ayrıca talimat verilmesi gerekmektedir.</p>
                      
                      <p>• Taksitli Avans işleminin yapıldığı gün söz konusu işlemle ilgili erken kapama veya kısmi kapama yapılamaz.</p>
                      
                      <p>• Kredi kartlarında gecikme, bloke, yasal takip olan Akbanklılar, Taksitli Avans hizmetinden faydalanamaz.</p>
                      
                      <p>• Ek kart sahipleri ayrı bir müşteri numarasına sahiplerse tüm ATM'lerden ve şubelerden Taksitli Avans işlemi yapabilir.</p>
                      
                      <p>• 08.10.2013 tarihinde yayınlanan Banka ve Kredi Kartları Hakkında Yönetmeliğin 22. maddesi ve 6 fıkrasındaki değişiklik kapsamında bir takvim yılı içerisinde 3 defa dönem borcunun asgari tutarının altında ödeme yapılmış olan kredi kartları ile dönem borcunun tamamı ödenene kadar nakit çekim işlemi gerçekleştirilemez.</p>
                      
                      <p>• Bu işlemlere ilişkin bekleyen tüm taksitlerin ödemeleri tamamlanıncaya kadar hesap kesim tarihinde değişiklik yapılamadığını bildirmek isteriz.</p>
                      
                      <p>• Bankanın kampanya koşullarını değiştirme, ek bilgi ve belge isteme ve kampanyayı durdurma hakkına sahip olduğunu hatırlatırız.</p>
                    </div>
                  </div>

                  {/* Genel Bilgiler */}
                  <div className="border-t border-slate-200 pt-6">
                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="text-sm text-slate-600 mb-4">
                        Üründen faydalanma koşulları ve ürün detay bilgileri ilgili finansal kurum tarafından belirlenmekte ve güncellenmektedir. Bu sayfada yer alan bilgiler kurumun belirlediği en güncel verileri içermeyebilir ancak başvuru sırasında güncel bilgiler kurum tarafından temin edilecektir. Kampanya şartları ve vadeli/vadesiz hesap işletim koşulları sadece ilgili kurum tarafından belirlendiğinden, bu ekranlarda yer alan bilgiler ile ilgili kurumun ekranlarındaki bilgiler arasında farklılık olabilir. Güncel bilgiler için kurumun sayfasını inceleyebilirsiniz.
                      </p>
                      
                      <div className="space-y-2">
                        <h4 className="font-medium text-slate-800">Genel bilgiler</h4>
                        <p className="text-sm text-slate-600">%0 Faizli 3 ay vadeli 25.000 TL'ye varan taksitli avans!</p>
                        <p className="text-sm text-slate-600">%0 Faizli 3 ay vadeli 65.000 TL'ye varan taksitli artı para!</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-slate-200">
                <div className="flex justify-end">
                  <button
                    onClick={() => setIsConditionsModalOpen(false)}
                    className="px-6 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                  >
                    Kapat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankLoanPage;